import { datasourcesSelector, datasourceStatusesSelector, stationDatasourceIdsSelector } from 'Features/App/Dictionaries/dictionariesSlice';
import {
    selectedDangerModeSelector, selectedDataReceivedSelector, selectedDatasourceTypesIdsSelector,
    selectedEnableModeSelector, selectedSoftReleaseSelector, selectedStormModeSelector,
} from 'Features/Filters/filtersSlice';
import { createSelector } from '@reduxjs/toolkit';
import { DatasourceType } from 'Services/api/v2';
import { DictionaryType } from 'helper/types';
import { CreateSelector } from 'Features/App/store';
import { FilterDatasources, getDatasourceIds } from '../../../Filters/Datasources/filters';
import {
    selectedDangerModeSelectorMonitoring, selectedDataReceivedSelectorMonitoring,
    selectedDatasourceTypesIdsSelectorMonitoring,
    selectedEnableModeSelectorMonitoring, selectedSoftReleaseSelectorMonitoring, selectedStormModeSelectorMonitoring
} from "../../Selectors/filtersSliceMonitoring";
import {datasourcesSelectorMonitoring} from "../../../App/Dictionaries/selectors/datasourcesMonitoring";
import {datasourceStatusesSelectorMonitoring} from "../../../App/Dictionaries/selectors/datasourceStatusMonitoring";

/**
 * фильтрация источников по выбранному:
 * * типу источника
 * * статусам
 * @returns `undefined` - когда не выбраны фильтры для источника
 */
const internalFilterDatasourcesSelectorMonitoring: CreateSelector<DictionaryType<DatasourceType> | undefined> = createSelector(
    [
        datasourcesSelectorMonitoring,
        selectedDatasourceTypesIdsSelectorMonitoring,
        datasourceStatusesSelectorMonitoring,
        selectedEnableModeSelectorMonitoring,
        selectedDangerModeSelectorMonitoring,
        selectedSoftReleaseSelectorMonitoring,
        selectedDataReceivedSelectorMonitoring,
        selectedStormModeSelectorMonitoring,
    ],
    (
        datasources,
        datasourceTypesIds,
        datasourceStatuses,
        enabledMode,
        dangerMode,
        softRelease,
        dataReceived,
        stormMode,
    ) => {
        // при отсутствии фильтров должно вернутся undefined, чтобы не происходила фильтрация по источникам, так как станции могут быть без источников
        const hasDatasourceFilters = [
            datasourceTypesIds, enabledMode, dangerMode, softRelease, dataReceived, stormMode,
        ].some((filter) => filter !== undefined);

        if (hasDatasourceFilters) {
            return FilterDatasources
                .setValues(Object.values(datasources))
                .byDatasourceTypeIds(datasourceTypesIds)
                .byEnabledMode(datasourceStatuses, enabledMode)
                .byDangerMode(datasourceStatuses, dangerMode)
                .bySoftRelease(datasourceStatuses, softRelease)
                .byDataReceived(datasourceStatuses, dataReceived)
                .byStormMode(datasourceStatuses, stormMode)
                .getDictionary();
        }
        return undefined;
    },
);
/**
 * @returns `undefined` - когда не выбраны фильтры для источника
 */
export const filteredDatasourcesIdsSelectorMonitoring: CreateSelector<number[] | undefined> = createSelector(
    internalFilterDatasourcesSelectorMonitoring,
    (datasources) => (datasources ? getDatasourceIds(datasources) : undefined),
);

/**
 * Фильтрация входного списка источников по выбранным статусам
 */
export const filterStationDatasourcesIdsByStatusesSelectorMonitoring = (stationId: number): CreateSelector<number[]> => createSelector(
    [
        stationDatasourceIdsSelector(stationId),
        datasourcesSelector,
        datasourceStatusesSelector,
        selectedEnableModeSelector,
        selectedDangerModeSelector,
        selectedSoftReleaseSelector,
        selectedDataReceivedSelector,
        selectedStormModeSelector,
    ],
    (
        stationDatasourcesIds,
        datasources,
        datasourceStatuses,
        enabledMode,
        dangerMode,
        softRelease,
        dataReceived,
        stormMode,
    ) => {
        const sources = stationDatasourcesIds.map((id) => datasources[id]);
        return FilterDatasources
            .setValues(Object.values(sources))
            .byEnabledMode(datasourceStatuses, enabledMode)
            .byDangerMode(datasourceStatuses, dangerMode)
            .bySoftRelease(datasourceStatuses, softRelease)
            .byDataReceived(datasourceStatuses, dataReceived)
            .byStormMode(datasourceStatuses, stormMode)
            .getIds();
    },
);
export const filterStationDatasourcesIdsByStatusesLengthSelectorMonitoring = (stationId: number): CreateSelector<number> => createSelector(
    filterStationDatasourcesIdsByStatusesSelectorMonitoring(stationId),
    (ids) => ids.length,
);
